import { Button, Spin, Divider, Collapse, Input, Tooltip } from 'antd';
import { ErrorMessage, Formik, Field } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { QuestionCircleOutlined } from '@ant-design/icons';

import { adminActions } from 'store/admin/slices';
import { FormikInputFieldWithLabel } from 'elements/FormikInputFieldWithLabel';
import palette from 'palette';
import { useGetSettings } from './hooks/use-get-settings';
import { useSettingsState } from './hooks/use-settings-state';
import {
  StyledButtons,
  StyledContent,
  StyledContentForm,
  StyledEditBlock,
  StyledErrorMessage,
  StyledSummary,
} from './styles';
import { SettingsFieldName, UpdateSettingsValues } from './types';
import { updateSettingsValidationSchema } from './validation-schema';
import { DEFAULT_OPACITY, DEFAULT_ROTATE, DEFAULT_REPEAT, DEFAULT_FONTSIZE, DEFAULT_COLOR } from './static';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { settings, isSettingsLoading, isUpdateSettingsLoading, settingsError } = useSettingsState();

  useGetSettings();

  const onSubmitSettings = (values: UpdateSettingsValues) => {
    if (!values.accessTokenExpiration) {
      return;
    }

    dispatch(adminActions.systemSettings.requestPostSettings(values as any));
  };

  return (
    <StyledContent>
      <StyledSummary>
        <Formik<UpdateSettingsValues>
          initialValues={{
            accessTokenExpiration: settings?.accessTokenExpiration,
            watermarkTemplate: settings?.watermarkTemplate || '',
            watermarkOpacity: settings?.watermarkOpacity || DEFAULT_OPACITY,
            watermarkRotation: settings?.watermarkRotation || DEFAULT_ROTATE,
            watermarkRepeatPeriod: settings?.watermarkRepeatPeriod || DEFAULT_REPEAT,
            watermarkFontSize: settings?.watermarkFontSize || DEFAULT_FONTSIZE,
            watermarkColor: settings?.watermarkColor || DEFAULT_COLOR,
          }}
          enableReinitialize
          validationSchema={updateSettingsValidationSchema}
          onSubmit={(submittedValues) => {
            onSubmitSettings({
              ...submittedValues,
            });
          }}
        >
          {({ values, handleChange, handleSubmit, errors }) => (
            <StyledContentForm onSubmit={handleSubmit}>
              <Spin spinning={isSettingsLoading || isUpdateSettingsLoading}>
                <StyledEditBlock>
                  <FormikInputFieldWithLabel
                    name={SettingsFieldName.accessTokenExpiration}
                    onChange={handleChange}
                    style={{ borderColor: errors.accessTokenExpiration ? palette.red.r3 : palette.grey.g5 }}
                    label={t('infoFields.accessTokenExpiration')}
                    defaultValue={values.accessTokenExpiration || 0}
                    type="number"
                  />

                  <ErrorMessage component={StyledErrorMessage} name={SettingsFieldName.accessTokenExpiration} />
                </StyledEditBlock>

                <Divider />

                <Collapse>
                  <Collapse.Panel header={t('settingsPage.watermark.title')} key="watermark">
                    <StyledEditBlock>
                      <label
                        htmlFor={SettingsFieldName.watermarkTemplate}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        {t('settingsPage.watermark.template')}
                        <Tooltip title={t('settingsPage.watermark.supportedPlaceholders')}>
                          <QuestionCircleOutlined style={{ fontSize: 12, color: palette.grey.g7 }} />
                        </Tooltip>
                      </label>
                      <Field
                        as={Input.TextArea}
                        id={SettingsFieldName.watermarkTemplate}
                        name={SettingsFieldName.watermarkTemplate}
                        rows={4}
                      />
                      <StyledErrorMessage>
                        <ErrorMessage name={SettingsFieldName.watermarkTemplate} />
                      </StyledErrorMessage>
                    </StyledEditBlock>

                    {/* opacity */}
                    <StyledEditBlock>
                      <FormikInputFieldWithLabel
                        name={SettingsFieldName.watermarkOpacity}
                        onChange={handleChange}
                        style={{ borderColor: errors.watermarkOpacity ? palette.red.r3 : palette.grey.g5 }}
                        label={
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {t('settingsPage.watermark.opacity')}
                            <Tooltip title={t('settingsPage.watermark.opacityHint')}>
                              <QuestionCircleOutlined style={{ fontSize: 12, color: palette.grey.g7 }} />
                            </Tooltip>
                          </span>
                        }
                        defaultValue={values.watermarkOpacity}
                        type="number"
                      />
                      <StyledErrorMessage>
                        <ErrorMessage name={SettingsFieldName.watermarkOpacity} />
                      </StyledErrorMessage>
                    </StyledEditBlock>

                    {/* rotation */}
                    <StyledEditBlock>
                      <FormikInputFieldWithLabel
                        name={SettingsFieldName.watermarkRotation}
                        onChange={handleChange}
                        style={{ borderColor: errors.watermarkRotation ? palette.red.r3 : palette.grey.g5 }}
                        label={
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {t('settingsPage.watermark.rotation')}
                            <Tooltip title={t('settingsPage.watermark.rotationHint')}>
                              <QuestionCircleOutlined style={{ fontSize: 12, color: palette.grey.g7 }} />
                            </Tooltip>
                          </span>
                        }
                        defaultValue={values.watermarkRotation}
                        type="number"
                      />
                      <StyledErrorMessage>
                        <ErrorMessage name={SettingsFieldName.watermarkRotation} />
                      </StyledErrorMessage>
                    </StyledEditBlock>

                    {/* repeat period */}
                    <StyledEditBlock>
                      <FormikInputFieldWithLabel
                        name={SettingsFieldName.watermarkRepeatPeriod}
                        onChange={handleChange}
                        style={{ borderColor: errors.watermarkRepeatPeriod ? palette.red.r3 : palette.grey.g5 }}
                        label={
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {t('settingsPage.watermark.repeatPeriod')}
                            <Tooltip title={t('settingsPage.watermark.repeatPeriodHint')}>
                              <QuestionCircleOutlined style={{ fontSize: 12, color: palette.grey.g7 }} />
                            </Tooltip>
                          </span>
                        }
                        defaultValue={values.watermarkRepeatPeriod}
                        type="number"
                      />
                      <StyledErrorMessage>
                        <ErrorMessage name={SettingsFieldName.watermarkRepeatPeriod} />
                      </StyledErrorMessage>
                    </StyledEditBlock>
                    <StyledEditBlock>
                      <FormikInputFieldWithLabel
                        name={SettingsFieldName.watermarkFontSize}
                        onChange={handleChange}
                        style={{ borderColor: errors.watermarkFontSize ? palette.red.r3 : palette.grey.g5 }}
                        label={
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {t('settingsPage.watermark.fontSize')}
                            <Tooltip title={t('settingsPage.watermark.fontSizeHint')}>
                              <QuestionCircleOutlined style={{ fontSize: 12, color: palette.grey.g7 }} />
                            </Tooltip>
                          </span>
                        }
                        defaultValue={values.watermarkFontSize}
                        type="number"
                      />
                      <StyledErrorMessage>
                        <ErrorMessage name={SettingsFieldName.watermarkFontSize} />
                      </StyledErrorMessage>
                    </StyledEditBlock>

                    {/* color */}
                    <StyledEditBlock>
                      <label htmlFor={SettingsFieldName.watermarkColor}>{t('settingsPage.watermark.color')}</label>
                      <Input
                        type="color"
                        name={SettingsFieldName.watermarkColor}
                        value={values.watermarkColor}
                        onChange={handleChange}
                        style={{ width: 80, height: 40, padding: 0, border: `1px solid ${palette.grey.g5}` }}
                      />

                      <StyledErrorMessage>
                        <ErrorMessage name={SettingsFieldName.watermarkColor} />
                      </StyledErrorMessage>
                    </StyledEditBlock>
                  </Collapse.Panel>
                </Collapse>
              </Spin>
              <StyledButtons>
                <Button
                  disabled={isSettingsLoading || isUpdateSettingsLoading}
                  htmlType="submit"
                  style={{ color: palette.white.w1, backgroundColor: palette.blue.b1 }}
                >
                  {t('common.apply')}
                </Button>
              </StyledButtons>
              {settingsError?.userMessage && <StyledErrorMessage>{settingsError.userMessage}</StyledErrorMessage>}
            </StyledContentForm>
          )}
        </Formik>
      </StyledSummary>
    </StyledContent>
  );
};

export default SettingsPage;
